import {
  AfterViewInit,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ProfissionalAusenciaService } from '@nomoh-monorepo/data-access-bardo-administrador';
import { NotificacaoService } from '@nomoh-monorepo/data-access-shared-notificacao';
import {
  DeviceService,
  FormFieldComponent,
  NFieldsetComponent,
} from '@nomoh-monorepo/shared-common-ui';
import { IProfissional } from '@nomoh-monorepo/shared-types';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'lib-ausencia-criar-editar',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    ButtonModule,
    DatePickerModule,
    InputTextModule,
    TextareaModule,
    FormFieldComponent,
    NFieldsetComponent,
    TranslateModule,
  ],
  templateUrl: './ausencia-criar-editar.component.html',
  styleUrls: ['./ausencia-criar-editar.component.scss'],
})
export class AusenciaCriarEditarComponent implements AfterViewInit, OnInit {
  //#region Input e Output
  @Input() profissional!: IProfissional;

  @ViewChild('footerTemplate', { static: false })
  private _footerTemplateRef!: TemplateRef<unknown>;

  @Output() footerTemplateAction = new EventEmitter<TemplateRef<unknown>>();
  @Output() salvo = new EventEmitter<void>();
  @Output() cancelar = new EventEmitter<void>();
  //#endregion

  //#region Injeção de Dependências
  private fb = inject(FormBuilder);
  private ausenciaService = inject(ProfissionalAusenciaService);
  private notificacaoService = inject(NotificacaoService);
  private translateService = inject(TranslateService);
  deviceService = inject(DeviceService);
  //#endregion

  //#region Signals
  $salvando = signal(false);
  //#endregion

  //#region Formulário
  form: FormGroup = this.fb.group({
    dataInicio: [null, Validators.required],
    horaInicio: [null, Validators.required],
    dataFim: [null, Validators.required],
    horaFim: [null, Validators.required],
    motivo: ['', [Validators.required, Validators.maxLength(300)]],
  });
  //#endregion

  ngOnInit(): void {
    let horaInicioVal: Date | string | null = null;
    let horaFimVal: Date | string | null = null;
    const isMobile = this.deviceService.$isMobile();

    if (this.profissional.inicioExpediente) {
      const [h, m] = this.profissional.inicioExpediente.split(':');
      if (isMobile) {
        horaInicioVal = `${String(+h).padStart(2, '0')}:${String(+m).padStart(
          2,
          '0'
        )}`;
      } else {
        const d = new Date();
        d.setHours(+h, +m, 0, 0);
        horaInicioVal = d;
      }
    }

    if (this.profissional.fimExpediente) {
      const [h, m] = this.profissional.fimExpediente.split(':');
      if (isMobile) {
        horaFimVal = `${String(+h).padStart(2, '0')}:${String(+m).padStart(
          2,
          '0'
        )}`;
      } else {
        const d = new Date();
        d.setHours(+h, +m, 0, 0);
        horaFimVal = d;
      }
    }

    this.form.patchValue({
      horaInicio: horaInicioVal,
      horaFim: horaFimVal,
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.footerTemplateAction.emit(this._footerTemplateRef);
    });
  }

  //#region Métodos
  async salvar(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { dataInicio, horaInicio, dataFim, horaFim, motivo } =
      this.form.value;

    const parseDateInput = (dateVal: Date | string | null): Date | null => {
      if (!dateVal) return null;
      if (dateVal instanceof Date) return dateVal;
      const parts = String(dateVal).split('-');
      if (parts.length === 3) {
        return new Date(
          parseInt(parts[0]),
          parseInt(parts[1]) - 1,
          parseInt(parts[2])
        );
      }
      return new Date(dateVal);
    };

    const parseTimeInput = (
      timeVal: Date | string | null
    ): { h: number; m: number } | null => {
      if (!timeVal) return null;
      if (timeVal instanceof Date)
        return { h: timeVal.getHours(), m: timeVal.getMinutes() };
      const parts = String(timeVal).split(':');
      if (parts.length >= 2) {
        return { h: parseInt(parts[0]), m: parseInt(parts[1]) };
      }
      return null;
    };

    const dInicio = parseDateInput(dataInicio);
    const tInicio = parseTimeInput(horaInicio);
    if (dInicio && tInicio) {
      dInicio.setHours(tInicio.h, tInicio.m, 0, 0);
    }

    const dFim = parseDateInput(dataFim);
    const tFim = parseTimeInput(horaFim);
    if (dFim && tFim) {
      dFim.setHours(tFim.h, tFim.m, 0, 0);
    }

    if (!dInicio || !dFim) return;

    if (dFim <= dInicio) {
      this.notificacaoService.notificacaoWarn(
        'Data/hora de fim deve ser após o início'
      );
      return;
    }

    if (!this.profissional?.id) {
      return;
    }

    this.$salvando.set(true);
    try {
      await this.ausenciaService.criar(this.profissional.id, {
        dataHoraInicio: dInicio,
        dataHoraFim: dFim,
        motivo: motivo.trim(),
      });

      this.notificacaoService.notificacaoSuccess(
        this.translateService.instant('BARDO_PROFISSIONAIS.AUSENCIAS_TAB.SUCESSO_REGISTRAR')
      );
      this.salvo.emit();
    } catch {
      this.notificacaoService.notificacaoError(
        this.translateService.instant('BARDO_PROFISSIONAIS.AUSENCIAS_TAB.ERRO_REGISTRAR')
      );
    } finally {
      this.$salvando.set(false);
    }
  }
  //#endregion
}
